import { useState, useEffect, useCallback } from 'react';
import { Shield, Search, Calendar, ExternalLink } from 'lucide-react';
import { api } from '@/lib/api';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

const inputCls = "bg-[#0A1F1A] border border-[#1A3B32] text-white placeholder:text-white/30 focus:border-[#A38560] focus:ring-0 rounded-xl h-11 text-sm";

const formatDateTime = (iso) => {
  if (!iso) return '-';
  try {
    const d = new Date(iso);
    return new Intl.DateTimeFormat('es-CO', {
      timeZone: 'America/Bogota',
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
    }).format(d);
  } catch { return iso; }
};

export default function PrivacyConsents() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState('');
  const [date, setDate] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set('q', q);
      if (date) params.set('date', date);
      const data = await api.get(`/privacy-consents${params.toString() ? '?' + params.toString() : ''}`);
      setItems(data);
    } catch { toast.error('Error al cargar consentimientos'); }
    setLoading(false);
  }, [q, date]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  return (
    <div className="space-y-6" data-testid="privacy-consents-page">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl bg-[#A38560]/10 border border-[#A38560]/20 flex items-center justify-center">
          <Shield size={20} className="text-[#A38560]" strokeWidth={1.5} />
        </div>
        <div>
          <h1 className="font-outfit text-3xl font-medium tracking-tight text-white">Habeas Data</h1>
          <p className="text-xs text-white/40 mt-0.5 tracking-wide">Registro de aceptaciones de la politica de tratamiento de datos personales</p>
        </div>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 bg-[#0A1F1A] rounded-2xl border border-white/5 p-4">
        <div className="relative md:col-span-2">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
          <Input data-testid="consents-search" value={q} onChange={e => setQ(e.target.value)}
            placeholder="Buscar por nombre o celular..." className={`${inputCls} pl-9`} />
        </div>
        <div className="relative min-w-0">
          <Calendar size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30 pointer-events-none" />
          <Input data-testid="consents-date" type="date" value={date} onChange={e => setDate(e.target.value)}
            className={`${inputCls} pl-9 w-full min-w-0 max-w-full appearance-none [color-scheme:dark]`} />
        </div>
      </div>

      {/* Counter */}
      <div className="flex items-center justify-between text-xs text-white/40">
        <span data-testid="consents-count">{items.length} aceptacion{items.length !== 1 ? 'es' : ''}{(q || date) ? ' (filtrado)' : ''}</span>
        {(q || date) && (
          <button onClick={() => { setQ(''); setDate(''); }} className="text-[#A38560] hover:underline" data-testid="clear-filters">
            Limpiar filtros
          </button>
        )}
      </div>

      {/* Table desktop */}
      <div className="hidden md:block bg-[#0A1F1A] rounded-2xl border border-white/5 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#03110D] border-b border-white/5">
                <th className="text-left px-4 py-3 font-medium text-white/40 text-[11px] uppercase tracking-wider">Nombre completo</th>
                <th className="text-left px-4 py-3 font-medium text-white/40 text-[11px] uppercase tracking-wider">Celular</th>
                <th className="text-left px-4 py-3 font-medium text-white/40 text-[11px] uppercase tracking-wider">Fecha</th>
                <th className="text-left px-4 py-3 font-medium text-white/40 text-[11px] uppercase tracking-wider">Hora (Bogota)</th>
                <th className="text-left px-4 py-3 font-medium text-white/40 text-[11px] uppercase tracking-wider">Formulario / Fuente</th>
                <th className="text-left px-4 py-3 font-medium text-white/40 text-[11px] uppercase tracking-wider">Politica</th>
              </tr>
            </thead>
            <tbody>
              {items.map(r => {
                const dt = r.accepted_at ? new Date(r.accepted_at) : null;
                const fecha = dt ? new Intl.DateTimeFormat('es-CO', { timeZone: 'America/Bogota', day: '2-digit', month: 'long', year: 'numeric' }).format(dt) : '-';
                const hora = dt ? new Intl.DateTimeFormat('es-CO', { timeZone: 'America/Bogota', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false }).format(dt) : '-';
                return (
                  <tr key={r.id} className="border-b border-white/5 hover:bg-white/[0.02] transition-colors" data-testid={`consent-row-${r.id}`}>
                    <td className="px-4 py-3.5 text-white font-medium">{r.full_name}</td>
                    <td className="px-4 py-3.5 text-white/70 tabular-nums" data-testid={`consent-phone-${r.id}`}>{r.phone || '-'}</td>
                    <td className="px-4 py-3.5 text-white/70">{fecha}</td>
                    <td className="px-4 py-3.5 text-[#A38560] tabular-nums font-medium">{hora}</td>
                    <td className="px-4 py-3.5 text-white/60 text-xs">{r.source || '-'}</td>
                    <td className="px-4 py-3.5">
                      <a href={r.policy_url} target="_blank" rel="noreferrer"
                        className="inline-flex items-center gap-1 text-[11px] text-[#A38560] hover:underline">
                        Ver politica <ExternalLink size={11} />
                      </a>
                    </td>
                  </tr>
                );
              })}
              {!loading && items.length === 0 && (
                <tr><td colSpan={6} className="text-center py-12 text-white/30 text-sm">
                  {(q || date) ? 'Sin resultados con los filtros actuales.' : 'Aun no hay aceptaciones registradas.'}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Mobile cards */}
      <div className="md:hidden space-y-3">
        {items.map(r => (
          <div key={r.id} className="bg-[#0A1F1A] rounded-2xl border border-white/5 p-4 space-y-2">
            <div className="flex items-start justify-between gap-3">
              <p className="text-sm text-white font-medium">{r.full_name}</p>
              <span className="text-[10px] text-[#A38560] uppercase tracking-wider shrink-0">{r.source || '-'}</span>
            </div>
            {r.phone && <a href={`tel:${r.phone}`} data-testid={`consent-phone-m-${r.id}`} className="block text-sm text-white/70 tabular-nums">{r.phone}</a>}
            <p className="text-[11px] text-white/40 font-mono">{formatDateTime(r.accepted_at)}</p>
            <a href={r.policy_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[11px] text-[#A38560] hover:underline">
              Ver politica <ExternalLink size={10} />
            </a>
          </div>
        ))}
        {!loading && items.length === 0 && (
          <div className="text-center py-12 text-white/30 text-sm bg-[#0A1F1A] rounded-2xl border border-white/5">
            {(q || date) ? 'Sin resultados.' : 'Aun no hay aceptaciones.'}
          </div>
        )}
      </div>
    </div>
  );
}
